export type PathToken = { type: 'prop'; key: string } | { type: 'index'; index: number };

// Tokenizes dot/bracket paths like:
// - `buyer.firstName`
// - `tradeIns[0].vin`
// into a sequence of {prop}/{index} tokens.
export function tokenizePath(path: string): PathToken[] {
    const tokens: PathToken[] = [];
    // Capture either:
    // - a plain property segment (anything except dot/brackets)
    // - a numeric bracket index (e.g. `[12]`)
    const re = /([^.[\]]+)|\[(\d+)\]/g;
    let match: RegExpExecArray | null;
    while ((match = re.exec(path)) !== null) {
        if (match[1]) {
            // Property segment
            tokens.push({ type: 'prop', key: match[1] });
        } else if (match[2]) {
            // Numeric index
            tokens.push({ type: 'index', index: Number(match[2]) });
        }
    }
    return tokens;
}

// Removes only numeric indices from a path: `a[0].b` -> `a.b`.
// This is used to derive stable rule keys from indexed evaluation paths.
export function stripIndices(path: string): string {
    return path.replace(/\[\d+\]/g, '');
}

/**
 * Expands a non-indexed base path against a values tree.
 *
 * Why this exists:
 * - Validation rules are commonly registered against non-indexed "rule keys" like `phones.number`.
 * - At runtime, `values.phones` may be an array, so we need concrete indexed evaluation paths
 *   like `phones[0].number`, `phones[1].number` to validate each element and store errors per field.
 *
 * Example:
 * - basePath: `phones.number`
 * - values.phones: [{ number: '1' }, { number: '2' }]
 * => [`phones[0].number`, `phones[1].number`]
 */
export function expandPathAgainstValues(basePath: string, values: any): string[] {
    const tokens = tokenizePath(basePath);

    // Depth-first walk over the token stream while carrying:
    // - `val`: the current value subtree at this point in the traversal
    // - `built`: the path string built so far (no leading dot)
    const walk = (val: any, tokenIndex: number, built: string): string[] => {
        // Base case: all tokens consumed. If nothing was built (e.g. empty path), return [].
        if (tokenIndex >= tokens.length) return built ? [built] : [];

        const token = tokens[tokenIndex];
        if (token.type === 'prop') {
            const nextVal = val ? (val as any)[token.key] : undefined;
            const nextToken = tokens[tokenIndex + 1];

            // Array-expansion heuristic:
            // - If `nextVal` is an array AND the next token is *not* an explicit `[n]` index,
            //   expand to every element and splice `[i]` into the built path.
            // - If the basePath already includes an index token (e.g. `phones[0].number`),
            //   we treat it as an explicit selection and do not expand.
            const shouldExpandArray = Array.isArray(nextVal) && nextToken?.type !== 'index';

            if (shouldExpandArray) {
                const out: string[] = [];
                for (let i = 0; i < nextVal.length; i++) {
                    // When we expand an array, we materialize the `[i]` index into the path.
                    const nextBuilt = built ? `${built}.${token.key}[${i}]` : `${token.key}[${i}]`;
                    out.push(...walk(nextVal[i], tokenIndex + 1, nextBuilt));
                }
                return out;
            }

            // Non-array property segment: append `.prop` (or `prop` if this is the first segment).
            const nextBuilt = built ? `${built}.${token.key}` : token.key;
            return walk(nextVal, tokenIndex + 1, nextBuilt);
        }

        // token.type === 'index'
        const arr = Array.isArray(val) ? val : undefined;
        const indexedVal = arr ? arr[token.index] : undefined;
        const nextBuilt = `${built}[${token.index}]`;
        return walk(indexedVal, tokenIndex + 1, nextBuilt);
    };

    return walk(values, 0, '');
}
